import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { appointmentsApi, type ConflictErrorResponse, type CreateAppointmentRequest, type UpdateAppointmentRequest } from '@/lib/api';
import { queryKeys } from '@/lib/query-client';
import { AxiosError } from 'axios';

export function useAppointment(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.appointment(id || ''),
    queryFn: async () => {
      const response = await appointmentsApi.getById(id!);
      return response.data;
    },
    enabled: !!id,
  });
}

// CAMADA 2: RACE CONDITION PROTECTION - Aggressive Polling
// Fetch availability data for a store on a specific date
// Uses aggressive polling (refetchInterval: 15s) to keep data fresh
export function useAvailability(
  storeId: string,
  date: string,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: queryKeys.availability(storeId, date, ''), // Empty string for time (fetching all slots)
    queryFn: async () => {
      const response = await appointmentsApi.getAvailability(storeId, date);
      return response.data;
    },
    enabled: options?.enabled !== false && !!storeId && !!date,
    staleTime: 0,
    refetchInterval: 15000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
}

// CAMADA 3: RACE CONDITION PROTECTION - JIT Availability Check
// Ephemeral query (no cache) that checks availability RIGHT BEFORE submit.
// staleTime: 0 → Always fetch fresh
// gcTime: 0 → Don't cache (immediately garbage collect)
export function useAvailabilityCheck(
  storeId?: string,
  date?: string,
  time?: string
) {
  return useQuery({
    queryKey: queryKeys.availability(storeId || '', date || '', time || ''),
    queryFn: async () => appointmentsApi.checkAvailability(storeId!, date!, time!),
    enabled: !!storeId && !!date && !!time,
    staleTime: 0,
    gcTime: 0,
  });
}

export function useCreateAppointment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateAppointmentRequest) => {
      const response = await appointmentsApi.create(data);
      return response.data;
    },
    onSuccess: async (appointment) => {
      // Gerar guest token automaticamente após criar agendamento
      try {
        const { data } = await appointmentsApi.generateGuestToken(
          appointment.id,
          appointment.customerEmail
        );
        localStorage.setItem('geely_guest_token', data.token);
      } catch (error) {
        console.error('Failed to generate guest token:', error);
      }

      // Invalidate appointments queries
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
    },
  });
}

export function useUpdateAppointment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateAppointmentRequest }) => {
      const response = await appointmentsApi.update(id, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
    },
  });
}

// CAMADA 3: Type guard para tratamento de conflito 409
export function isConflictError(
  error: unknown
): error is AxiosError<ConflictErrorResponse> {
  return error instanceof AxiosError && error.response?.status === 409;
}
